/**
 * Clearwater positions for one business day (or one week's close), against the
 * previous one.
 *
 * This is the Fixed Income tab's Weekly and Daily perspective. It shows ONE side
 * of the recon on purpose: every custodian feed behind the month-end recon is a
 * month-end file, so between month-ends there is nothing to reconcile against.
 * What there is, is the Clearwater book every business day — and a paydown or a
 * trade that will break against next month's custodian file is visible here
 * weeks earlier.
 *
 * The header says "Clearwater" for that reason: a two-sided recon and a one-sided
 * position movement report answer different questions, and mistaking one for the
 * other is the failure mode worth designing against.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import {
  GridConfigMenu,
  PamGrid,
  SummaryStatsBar,
  notifyPermissionError,
  useGridConfigs,
  useNotification,
  useSummaryStats,
} from '@1823-partners/core';
import { SetFilterModule } from 'ag-grid-enterprise';

import api, { fetchDailyPositionDates, fetchDailyPositions } from '../../api';
import {
  GRANULARITY_LABELS, buildPeriodOptions, resolvePeriod, toIsoDay,
} from '../../utils/dailyPeriods';
import PeriodSelect from './PeriodSelect';

/** A position, not a lot: the same grain the month-end recon's CW side uses. */
const getPositionRowId = (data) =>
  [data.CW_CUST, data.CW_ACCT_ID, data.CW_SRC_ACCT, data.CW_SEC_ID, data.SEC_ID]
    .map((part) => part || '')
    .join('|');

/**
 * A position appearing or disappearing is a units move as much as one that
 * changed size — all three are what a recon has to look at, and all three are
 * what REPRICED is not.
 */
const UNITS_MOVE_STATUSES = ['UNITS CHANGED', 'NEW', 'REMOVED'];
const isUnitsMove = (row) => UNITS_MOVE_STATUSES.includes(row?.CHANGE_STATUS);

/** Module scope so the summary calculations can memoize on [] honestly. */
const countStatus = (status) => (data) =>
  data.filter((row) => row.CHANGE_STATUS === status).length;

/**
 * How far a position moved, in units.
 *
 * A NEW or REMOVED position's movement is its whole size. The server zero-fills
 * the absent side so the difference carries it, but this falls back to deriving
 * it — the two repos deploy independently, and against a server that still sends
 * a null difference the total would silently omit the largest moves on screen.
 *
 * A day with no baseline is excluded outright: nothing is known to have changed,
 * and deriving there would report the entire book as having moved in.
 */
const asNumber = (value) => {
  // Number(null) is 0, not NaN, so an absent amount has to be rejected before
  // the conversion — otherwise a missing difference reads as "did not move".
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const unitsMovement = (row) => {
  if (row.CHANGE_STATUS === 'NO BASELINE') return 0;
  const diff = asNumber(row.CURR_UNITS_DIFF);
  if (diff !== null) return diff;
  return (asNumber(row.CW_CURR_UNITS) || 0) - (asNumber(row.PRIOR_CURR_UNITS) || 0);
};

const number = (params) =>
  params.value != null ? Number(params.value).toLocaleString() : params.value;

const money = (params) =>
  params.value != null
    ? Number(params.value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    : params.value;

const round0 = (value) => Number(value || 0).toLocaleString(undefined, {
  maximumFractionDigits: 0,
});

const DailyPositions = ({ granularity = 'daily', height = 700 }) => {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const gridRef = useRef(null);

  const [dateRows, setDateRows] = useState([]);
  const [selectedValue, setSelectedValue] = useState('');
  // Rows are stored WITH the period they belong to, and the rendered set is
  // derived by matching that against the current selection. Holding a bare array
  // left the previous period's positions on screen under the new period's title,
  // summary and export filename while the request was in flight — the same trap
  // RemittanceDashboard documents for its run history.
  const [loaded, setLoaded] = useState({ asofdt: null, compareTo: null, rows: [] });
  // True from the first render: this component always fetches the date list on
  // mount, and starting at false rendered a grid of zero positions with a
  // working export while that request was in flight — a loading book presented
  // as an empty one.
  const [isLoadingDates, setIsLoadingDates] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [unitsMovesOnly, setUnitsMovesOnly] = useState(false);
  // A failed read is NOT an empty day. Storing [] for one made the grid, the
  // summary and the export indistinguishable from a real zero-position response
  // as soon as the toast cleared, so the failure is held until it is resolved.
  const [loadError, setLoadError] = useState(null);
  const [datesError, setDatesError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [noSnapshots, setNoSnapshots] = useState(false);

  // Both of these are keyed through the same normalizer as the period options.
  // Keying one from the raw payload meant a stamped date ('2026-08-07T00:00:00Z')
  // would never match a normalized lookup, and the restatement warning would go
  // quiet exactly when it was needed.
  const availableDates = useMemo(
    () => dateRows.map((row) => toIsoDay(row.ASOFDT)).filter(Boolean),
    [dateRows],
  );

  /** Which as-of dates arrived more than once, so the picker can say so. */
  const restatedDates = useMemo(
    () => new Set(
      dateRows
        .filter((row) => Number(row.N_LOADS) > 1)
        .map((row) => toIsoDay(row.ASOFDT))
        .filter(Boolean),
    ),
    [dateRows],
  );

  const loadDates = useCallback(async () => {
    try {
      setIsLoadingDates(true);
      setDatesError(null);
      const response = await fetchDailyPositionDates();
      const rows = Array.isArray(response.data) ? response.data : [];
      setDateRows(rows);
      setNoSnapshots(rows.length === 0);
    } catch (error) {
      console.error('Error fetching daily position dates:', error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification('Failed to load the daily position dates. Please try again.', 'error');
      }
      // Never fabricate dates: a guessed date maps to no data and reads as an
      // empty book rather than as a failed request. For the same reason the
      // failure is held — without it the grid renders zero positions with a
      // working export, which is indistinguishable from Clearwater having sent
      // nothing.
      setDateRows([]);
      setDatesError(error);
    } finally {
      setIsLoadingDates(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadDates();
  }, [loadDates]);

  const period = useMemo(
    () => resolvePeriod(availableDates, granularity, selectedValue),
    [availableDates, granularity, selectedValue],
  );

  const options = useMemo(
    () => buildPeriodOptions(availableDates, granularity).map((option) => ({
      ...option,
      restated: restatedDates.has(option.value),
    })),
    [availableDates, granularity, restatedDates],
  );

  const asofdt = period?.asofdt || '';
  const compareTo = period?.compareTo || null;

  useEffect(() => {
    if (!asofdt) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const response = await fetchDailyPositions(asofdt, compareTo);
        if (cancelled) return;
        setLoaded({
          asofdt,
          compareTo,
          rows: Array.isArray(response.data) ? response.data : [],
        });
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching daily positions:', error);
        // Drop the previous day's rows: leaving them on screen under the new
        // date's header would show one day's positions labelled as another's.
        setLoaded({ asofdt: null, compareTo: null, rows: [] });
        setLoadError(error);
        if (!notifyPermissionError(showNotification, error)) {
          showNotification('Failed to load the daily positions. Please try again.', 'error');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [asofdt, compareTo, reloadToken, showNotification]);

  const rowData = useMemo(
    () => (loaded.asofdt === asofdt && loaded.compareTo === compareTo ? loaded.rows : []),
    [loaded, asofdt, compareTo],
  );

  const priorHeader = useCallback(
    (name) => (compareTo ? `${name} ${compareTo}` : `${name} (prior)`),
    [compareTo],
  );

  const diffCellStyle = useCallback((params) => {
    const value = Number(params.value);
    if (!Number.isFinite(value) || value === 0) return null;
    return { color: value < 0 ? theme.palette.error.main : theme.palette.success.main };
  }, [theme]);

  const columnDefs = useMemo(() => [
    { field: 'CW_CUST', headerName: 'Custodian', filter: 'agSetColumnFilter', pinned: 'left', minWidth: 130 },
    { field: 'CW_SRC_ACCT', headerName: 'Account Number', filter: 'agSetColumnFilter', pinned: 'left', minWidth: 150 },
    { field: 'CW_SEC_ID', headerName: 'CW ID', filter: 'agSetColumnFilter', pinned: 'left', minWidth: 120 },
    {
      field: 'CHANGE_STATUS',
      headerName: 'Status',
      filter: 'agSetColumnFilter',
      minWidth: 140,
      cellStyle: (params) => (isUnitsMove(params.data)
        ? { backgroundColor: 'rgba(255, 165, 0, 0.3)' }
        : null),
    },
    { field: 'CW_SEC_DES', headerName: 'Description', filter: 'agSetColumnFilter', minWidth: 220 },
    { field: 'CLIENT_SHORT_NAME', headerName: 'CW Account', filter: 'agSetColumnFilter' },
    { field: 'CW_N_LOT', headerName: 'Lots', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: number },
    { field: 'AT1', headerName: 'Asset Type L1', filter: 'agSetColumnFilter' },
    { field: 'AT2', headerName: 'Asset Type L2', filter: 'agSetColumnFilter', hide: true },
    { field: 'AT3', headerName: 'Asset Type L3', filter: 'agSetColumnFilter', hide: true },

    // Units — the amounts this desk reconciles on, so they lead.
    { field: 'CW_CURR_UNITS', headerName: 'Curr Units', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: number },
    { field: 'PRIOR_CURR_UNITS', headerName: priorHeader('Curr Units'), type: 'number', filter: 'agNumberColumnFilter', valueFormatter: number },
    { field: 'CURR_UNITS_DIFF', headerName: 'Units Diff', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: number, cellStyle: diffCellStyle },
    { field: 'CW_ORIG_UNITS', headerName: 'Orig Units', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: number, hide: true },

    // Values — a reprice is not a break, so these follow the units.
    { field: 'CW_BV', headerName: 'Book Value', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: money },
    { field: 'PRIOR_BV', headerName: priorHeader('Book Value'), type: 'number', filter: 'agNumberColumnFilter', valueFormatter: money, hide: true },
    { field: 'BV_DIFF', headerName: 'BV Diff', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: money, cellStyle: diffCellStyle },
    { field: 'CW_MV', headerName: 'Market Value', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: money },
    { field: 'PRIOR_MV', headerName: priorHeader('Market Value'), type: 'number', filter: 'agNumberColumnFilter', valueFormatter: money, hide: true },
    { field: 'MV_DIFF', headerName: 'MV Diff', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: money, cellStyle: diffCellStyle },
    { field: 'CW_CV', headerName: 'Carry Value', type: 'number', filter: 'agNumberColumnFilter', valueFormatter: money, hide: true },

    { field: 'CW_ACCT_ID', headerName: 'CW Acct ID', filter: 'agSetColumnFilter', hide: true },
    { field: 'SEC_ID', headerName: 'Security ID', filter: 'agSetColumnFilter', hide: true },
    { field: 'ASOFDT', headerName: 'As of', filter: 'agSetColumnFilter', hide: true },
    { field: 'PRIOR_ASOFDT', headerName: 'Compared with', filter: 'agSetColumnFilter', hide: true },
  ], [priorHeader, diffCellStyle]);

  // Computed over the whole day, not the filtered view, so the bar keeps
  // reporting the day's totals while the grid is narrowed to the moves.
  const calculations = useMemo(() => ({
    totalRows: { label: 'Positions', calculate: (data) => data.length, color: 'primary' },
    unitsChanged: { label: 'Units Changed', calculate: countStatus('UNITS CHANGED'), color: 'error' },
    added: { label: 'New', calculate: countStatus('NEW'), color: 'warning' },
    removed: { label: 'Removed', calculate: countStatus('REMOVED'), color: 'warning' },
    repriced: { label: 'Repriced', calculate: countStatus('REPRICED'), color: 'info' },
    netUnits: {
      label: 'Net Units Change',
      calculate: (data) => data.reduce((sum, row) => sum + unitsMovement(row), 0),
      format: round0,
      color: 'primary',
    },
  }), []);
  const { stats: summaryStats } = useSummaryStats({ rowData, calculations });

  const unitsMoveCount = useMemo(() => rowData.filter(isUnitsMove).length, [rowData]);
  const displayedRowData = useMemo(
    () => (unitsMovesOnly ? rowData.filter(isUnitsMove) : rowData),
    [rowData, unitsMovesOnly],
  );

  const gridConfigs = useGridConfigs(api, {
    app: 'recon',
    gridId: `daily-positions-${granularity}`,
    gridApiRef: gridRef,
  });

  const busy = isLoading || isLoadingDates;

  const periodLabel = granularity === 'weekly' ? 'Week' : 'As of';

  /* A restated comparison day moves the difference for a reason that is not
     trading, exactly as a restated as-of day does — so both are called out, not
     just the one showing in the picker. */
  const comparisonNote = [
    period?.comparisonLabel,
    compareTo && restatedDates.has(compareTo) ? '(comparison day restated)' : '',
    asofdt && restatedDates.has(asofdt) ? '(this day restated)' : '',
  ].filter(Boolean).join(' ');

  const customToolbarComponents = useMemo(() => [
    <PeriodSelect
      key="period-select"
      label={periodLabel}
      options={options}
      value={asofdt}
      onChange={setSelectedValue}
      disabled={isLoadingDates || isLoading}
    />,
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [periodLabel, options, asofdt, isLoadingDates, isLoading, gridConfigs]);

  const customToolbarButtons = useMemo(() => [
    {
      label: `Units Changed (${unitsMoveCount})`,
      onClick: () => setUnitsMovesOnly((value) => !value),
      disabled: unitsMoveCount === 0 && !unitsMovesOnly,
      tooltip: unitsMovesOnly
        ? 'Showing the positions whose units moved — click to show the whole day'
        : `Filter to the ${unitsMoveCount} position(s) whose units moved, appeared or dropped`,
      sx: {
        color: unitsMovesOnly ? theme.palette.common.white : theme.palette.warning.main,
        borderColor: theme.palette.warning.main,
        backgroundColor: unitsMovesOnly ? theme.palette.warning.main : 'transparent',
        border: `1px solid ${theme.palette.warning.main}`,
        '&:hover': {
          backgroundColor: unitsMovesOnly ? theme.palette.warning.dark : 'rgba(255, 167, 38, 0.15)',
        },
        '&:disabled': { color: theme.palette.grey[400], borderColor: theme.palette.grey[300] },
      },
    },
  ], [unitsMovesOnly, unitsMoveCount, theme]);

  if (datesError) {
    return (
      <Box sx={{ height, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" disabled={isLoadingDates} onClick={loadDates}>
              Retry
            </Button>
          }
        >
          Could not load the list of Clearwater snapshot dates, so there is no period to show.
          The Monthly perspective reads the month-end recon and is unaffected.
        </Alert>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ height, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <PeriodSelect
            label={periodLabel}
            options={options}
            value={asofdt}
            onChange={setSelectedValue}
            disabled={isLoading}
          />
        </Stack>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={isLoading}
              onClick={() => setReloadToken((token) => token + 1)}
            >
              Retry
            </Button>
          }
        >
          {`Could not load the Clearwater positions for ${asofdt}. Nothing is shown rather than an `
            + 'empty grid: a failed read and a day with no positions would otherwise look the same.'}
        </Alert>
      </Box>
    );
  }

  if (noSnapshots) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        No daily position snapshots are available from Clearwater yet, so there is nothing to show
        at this granularity. The Monthly perspective reads the month-end recon and is unaffected.
      </Alert>
    );
  }

  return (
    <Box sx={{ height, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
        Clearwater positions only — the custodian files behind the month-end recon are delivered
        monthly, so between month-ends there is no custodian side to reconcile against. What moved
        here is what will break against the next custodian file.
      </Typography>
      {/* Zeros while a read is in flight read as a book with nothing in it. */}
      {!busy && <SummaryStatsBar stats={summaryStats} />}
      <PamGrid
        ref={gridRef}
        rowData={displayedRowData}
        columnDefs={columnDefs}
        getRowId={getPositionRowId}
        height={`${height - 240}px`}
        showToolbar
        title={`${GRANULARITY_LABELS[granularity] || ''} Clearwater Positions${period?.label ? ` — ${period.label}` : ''}`}
        subtitle={comparisonNote}
        showExport
        exportConfig={{
          fileName: `ClearwaterPositions_${granularity}_${asofdt || 'latest'}.xlsx`,
          sheetName: 'Positions',
        }}
        customToolbarComponents={customToolbarComponents}
        customToolbarButtons={customToolbarButtons}
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}
        showQuickFilter
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}
        quickFilterPlaceholder="Quick Filter..."
        isLoading={busy}
        loadingMessage={isLoadingDates ? 'Loading available dates...' : 'Loading daily positions...'}
        modules={[SetFilterModule]}
        showLegend
        legendItems={[
          {
            color: 'rgba(255, 165, 0, 0.3)',
            border: '1px solid rgba(255, 165, 0, 0.5)',
            label: 'Orange: units moved, appeared or dropped since the comparison date',
          },
        ]}
      />
    </Box>
  );
};

export default DailyPositions;
